/**
 * IndexedDB service for persisting draft form data and images.
 *
 * Database: vvsi-draft-db (v1)
 *   Store: draft-metadata  — key: 'current'
 *     Holds: draftId, caseId, status, formValues, sectionStates, hasTriedSubmit
 *
 *   Store: draft-images    — keyPath: 'id'
 *     Holds: id, sectionId, type, name, mimeType, blob (Blob)
 */

import { openDB, type IDBPDatabase } from 'idb'
import type { FieldMetadata, FieldResponsePayload, FormValues, MappedField, SectionState } from '@/types'
import type { RefOption } from '@/services/refService'
import type { AttributeDefinition } from '@/services/attributeService'

// ── Schema ───────────────────────────────────────────────────────────────────

const DB_NAME = 'vvsi-draft-db'
const DB_VERSION = 4          // bumped from 3 → 4 to add serverName field to draft-images
const STORE_METADATA = 'draft-metadata'
const STORE_IMAGES = 'draft-images'
const STORE_REF_CACHE = 'ref-cache'
const STORE_ATTR_CACHE = 'attr-cache'

export interface DraftMetadata {
  draftId: string | null
  caseId: string | null
  status: 'draft' | 'submitted' | 'processing' | 'complete'
  formValues: FormValues
  sectionStates: Record<string, SectionState>
  hasTriedSubmit: boolean
  isDirty?: boolean
  fieldMetadata?: Record<string, Record<string, FieldMetadata>>
  aiResults?: Record<string, MappedField[]>
  aiRawPayloads?: Record<string, FieldResponsePayload>
  ocrResults?: Record<string, MappedField[]>
  photoResults?: Record<string, MappedField[]>
}

export interface ImageRecord {
  id: string
  sectionId: string
  type: 'capture' | 'upload'
  name?: string
  /** UUID-prefixed blob storage name persisted so serverName survives page reload */
  serverName?: string
  /** Blob storage section path used for deletes: 'ai/{sectionId}', 'ocr/{sectionId}', or 'documents' (Form Recognizer). */
  blobSection?: string
  mimeType?: string
  blob: Blob
  /** SHA-256 hex digest of the file content. Persisted so duplicate detection survives page refresh. */
  contentHash?: string
}

// ── Database singleton ────────────────────────────────────────────────────────

let _db: Promise<IDBPDatabase> | null = null

function openDBConnection(): Promise<IDBPDatabase> {
  return openDB(DB_NAME, DB_VERSION, {
    upgrade(db) {
      if (!db.objectStoreNames.contains(STORE_METADATA)) {
        db.createObjectStore(STORE_METADATA)
      }
      if (!db.objectStoreNames.contains(STORE_IMAGES)) {
        db.createObjectStore(STORE_IMAGES, { keyPath: 'id' })
      }
      if (!db.objectStoreNames.contains(STORE_REF_CACHE)) {
        db.createObjectStore(STORE_REF_CACHE, { keyPath: 'category' })
      }
      if (!db.objectStoreNames.contains(STORE_ATTR_CACHE)) {
        db.createObjectStore(STORE_ATTR_CACHE, { keyPath: 'category' })
      }
    },
    terminated() {
      // Browser closed the connection (e.g. another tab upgraded the DB).
      // Clear the singleton so the next call re-opens it.
      _db = null
    },
  })
}

async function getDB(): Promise<IDBPDatabase> {
  if (!_db) {
    _db = openDBConnection()
  }
  try {
    return await _db
  } catch {
    // Opening failed — reset and try once more
    _db = openDBConnection()
    return _db
  }
}

// ── Draft metadata ────────────────────────────────────────────────────────────

export async function saveDraftMetadata(data: DraftMetadata): Promise<void> {
  const db = await getDB()
  await db.put(STORE_METADATA, data, 'current')
}

export async function loadDraftMetadata(): Promise<DraftMetadata | undefined> {
  const db = await getDB()
  return db.get(STORE_METADATA, 'current')
}

export async function clearDraftMetadata(): Promise<void> {
  const db = await getDB()
  await db.delete(STORE_METADATA, 'current')
}

// ── Image records ─────────────────────────────────────────────────────────────

export async function saveImageRecord(record: ImageRecord): Promise<void> {
  const db = await getDB()
  await db.put(STORE_IMAGES, record)
}

export async function loadAllImageRecords(): Promise<ImageRecord[]> {
  const db = await getDB()
  return db.getAll(STORE_IMAGES)
}

export async function deleteImageRecord(id: string): Promise<void> {
  const db = await getDB()
  await db.delete(STORE_IMAGES, id)
}

export async function clearAllImageRecords(): Promise<void> {
  const db = await getDB()
  await db.clear(STORE_IMAGES)
}

export async function updateImageServerName(id: string, serverName: string, blobSection?: string): Promise<void> {
  const db = await getDB()
  const record = await db.get(STORE_IMAGES, id)
  if (record) await db.put(STORE_IMAGES, { ...record, serverName, ...(blobSection ? { blobSection } : {}) })
}

// ── Combined clear ────────────────────────────────────────────────────────────

export async function clearAllDraftData(): Promise<void> {
  await Promise.all([clearDraftMetadata(), clearAllImageRecords(), clearRefCache(), clearAttrCacheRecord()])
}

// ── Ref-master cache ──────────────────────────────────────────────────────────

export interface RefCacheRecord {
  /** keyPath — matches the API category string (e.g. 'TRUCK_MAKE') */
  category: string
  options: RefOption[]
  cachedAt: number
}

export async function getRefCacheRecord(
  category: string
): Promise<RefCacheRecord | undefined> {
  const db = await getDB()
  return db.get(STORE_REF_CACHE, category)
}

export async function setRefCacheRecord(record: RefCacheRecord): Promise<void> {
  const db = await getDB()
  await db.put(STORE_REF_CACHE, record)
}

export async function clearRefCache(): Promise<void> {
  const db = await getDB()
  await db.clear(STORE_REF_CACHE)
}

// ── Attribute-master cache ─────────────────────────────────────────────────────

export interface AttrCacheRecord {
  /** keyPath — e.g. 'TRUCK_BODY_AMBULANCE' */
  category: string
  attributes: AttributeDefinition[]
  cachedAt: number
}

export async function getAttrCacheRecord(
  category: string
): Promise<AttrCacheRecord | undefined> {
  const db = await getDB()
  return db.get(STORE_ATTR_CACHE, category)
}

export async function setAttrCacheRecord(record: AttrCacheRecord): Promise<void> {
  const db = await getDB()
  await db.put(STORE_ATTR_CACHE, record)
}

export async function clearAttrCacheRecord(): Promise<void> {
  const db = await getDB()
  await db.clear(STORE_ATTR_CACHE)
}
